import { useCompositionForm, useCompositionsList } from "@design-lab/entities/composition";
import { useTab } from "@design-lab/shared/ui/layouts/state";
import { useCombinedProductData } from "@design-lab/entities/product";
import { trackCompositionValidationFailed } from "@design-lab/shared/lib/analytics";

export function useCompositionValidation() {
  const { form: compositionForm, registerValidationForAllCompositions } = useCompositionForm();
  const { formCompositions, compositions } = useCompositionsList();
  const { setTab } = useTab();
  const { inventoryData } = useCombinedProductData();

  const validateCompositions = async (): Promise<boolean> => {
    // Ensure validation is registered for all compositions before triggering
    registerValidationForAllCompositions();

    const isValid = await compositionForm.trigger("addedCompositions");

    if (!isValid) {
      const firstErrorIndex = Object.keys(compositionForm.formState.errors.addedCompositions || {})
        .map(Number)
        .sort((a, b) => a - b)[0];

      if (firstErrorIndex !== undefined && formCompositions[firstErrorIndex]) {
        const firstEmptyCompositionId = formCompositions[firstErrorIndex].id;

        // Track validation failure
        trackCompositionValidationFailed({
          addedCompositions: formCompositions,
          failedCompositionId: firstEmptyCompositionId,
          failedCompositionIndex: firstErrorIndex,
          formErrors: compositionForm.formState.errors
        });

        compositionForm.setValue("currentCompositionId", firstEmptyCompositionId);
        setTab("size");
      }
      return false;
    }

    // Validate inventory for each composition
    const inventoryErrors = formCompositions
      .flatMap(formComposition => {
        if (!inventoryData) return [];
        const composition = compositions?.find(c => c.id === formComposition.id);
        if (!composition) return [];

        return Object.entries((formComposition.variants as Record<string, number>) || {})
          .filter(([, quantity]) => Number(quantity) > 0)
          .map(([variantId, quantity]) => {
            const requestedQuantity = Math.max(0, Number(quantity) || 0);
            const availableQuantity = Math.max(
              0,
              (inventoryData as Record<string, number>)[variantId] || 0
            );

            if (requestedQuantity <= 0 || requestedQuantity <= availableQuantity) return null;

            const variant = composition.variants?.find(v => v.id === variantId);
            return {
              compositionId: formComposition.id,
              variantId,
              size: variant?.size || "Unknown",
              requested: requestedQuantity,
              available: availableQuantity
            };
          })
          .filter((error): error is NonNullable<typeof error> => error !== null);
      })
      .flat();

    if (inventoryErrors.length > 0) {
      compositionForm.setValue("inventoryErrors", inventoryErrors);

      const firstErrorComposition = formCompositions.find(formComposition =>
        inventoryErrors.some(error => error.compositionId === formComposition.id)
      );

      if (firstErrorComposition) {
        compositionForm.setValue("currentCompositionId", firstErrorComposition.id);
        setTab("size");
      }
      return false;
    }

    compositionForm.setValue("inventoryErrors", []);
    return true;
  };

  return { validateCompositions };
}
